import React, { useEffect } from 'react';
import { greenCheck, redCross } from '../../assets/hostedassets';

interface PasswordStrengthCheckerProps {
    password: string;
    onValidationChange?: (isValid: boolean) => void;
    isFocused: boolean;
}

const PasswordStrengthChecker = ({ password, onValidationChange, isFocused }: PasswordStrengthCheckerProps) => {
    // Define checks/rules as key value pair
    const hasTyped = password.length > 0;
    const rules = {
        length: {
            label: 'At Least 12 Characters',
            passed: password.length >= 12,
        },
        lower: {
            label: 'Lower Case Letter (a-z)',
            passed: /[a-z]/.test(password),
        },
        upper: {
            label: 'Upper Case Letter (A-Z)',
            passed: /[A-Z]/.test(password),
        },
        special: {
            label: 'Special Characters (e.g. !@#$%^&*)',
            passed: /[!@#$%^&*(),.?\":{}|<>]/.test(password),
        },
    };

    // Check if all rules are passed
    const isValid = Object.values(rules).every((rule) => rule.passed);

    // Notify parent about validation change
    useEffect(() => {
        onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    if (!isFocused) return null;

    return (
        isFocused && (
            <div>
                <p style={{ color: 'grey', fontSize: '12px' }}>Your password must contain:</p>
                {Object.entries(rules).map(([key, rule]) => (
                    <ConditionItem key={key} text={rule.label} passed={rule.passed} hasTyped={hasTyped} />
                ))}
            </div>
        )
    );
};

interface ConditionItemProps {
    text: string;
    passed: boolean;
    hasTyped: boolean;
}

const ConditionItem = ({ text, passed, hasTyped }: ConditionItemProps) => {
    const baseStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
    };

    if (!hasTyped) {
        return (
            <p style={{ ...baseStyle, color: 'gray' }}>
                <span style={{ marginRight: '8px' }}>–</span>
                {text}
            </p>
        );
    }

    return (
        <p style={{ ...baseStyle, color: passed ? '#33cc33' : '#DF666A' }}>
            <img src={passed ? greenCheck : redCross} alt={passed ? 'passed' : 'failed'} />
            {text}
        </p>
    );
};

export default PasswordStrengthChecker;
